import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useGantt, type GanttFilters, type GanttProject, type GanttSection, type GanttTask, type GanttUtilisation } from '../lib/gantt';
import { useUsersLookup } from '../lib/lookups';
import { apiErrorMessage } from '../lib/api';
import { isTaskDelayed } from '../lib/delay';
import { usePersistedState } from '../lib/usePersistedState';

dayjs.extend(isoWeek);

type ZoomLevel = 'week' | 'month';

// Status-Farben für die UNTERE Balken-Reihe ("Unser von/bis"). Identisch
// mit den Status-Selects in der Detail-Tabelle (siehe tokens.css).
const STATUS_INTERNAL_COLOR: Record<string, string> = {
  open:        'var(--status-open-border)',
  planning:    'var(--status-planning-border)',
  in_progress: 'var(--status-progress-border)',
  delayed:     'var(--status-delayed-border)',
  completed:   'var(--status-completed-border)',
};

// Bauzeit-Balken sind immer Stromo-Blau — unabhängig vom Status.
const SCHEDULE_BAR_COLOR = 'var(--status-progress-border)';

const WEEK_STEP = 4;
const WEEK_MIN = 4;
const WEEK_MAX = 52;
const MONTH_STEP = 2;
const MONTH_MIN = 2;
const MONTH_MAX = 24;

// Mindestbreite einer Spalte (px). Wird auch im JS für die Gesamtbreite des
// scrollbaren Tracks gebraucht, damit Monats-Header und KW-Header gleich breit
// bleiben — sonst driftet bei vielen Wochen das obere Header weg vom unteren.
const COLUMN_MIN_WIDTH_WEEK = 72;
const COLUMN_MIN_WIDTH_MONTH = 130;
const LABEL_WIDTH_PX = 336; // entspricht --gantt-label-width

export function GanttPage() {
  const [zoom, setZoom] = usePersistedState<ZoomLevel>('gantt.zoom', 'week');
  const [anchor, setAnchor] = useState<Dayjs>(dayjs().startOf('isoWeek'));
  const [pmFilter, setPmFilter] = useState<number | ''>('');
  const [smFilter, setSmFilter] = useState<number | ''>('');
  const [showTasks, setShowTasks] = usePersistedState<boolean>('gantt.showTasks', true);
  const [weekCount, setWeekCount] = usePersistedState<number>('gantt.weekCount', 16);
  const [monthCount, setMonthCount] = usePersistedState<number>('gantt.monthCount', 6);

  const columns = useMemo(() => {
    if (zoom === 'week') {
      const items: { key: string; start: Dayjs; label: string }[] = [];
      for (let i = 0; i < weekCount; i++) {
        const start = anchor.add(i, 'week');
        items.push({
          key: start.format('GGGG-[W]WW'),
          start,
          label: `KW${start.isoWeek()}`,
        });
      }
      return items;
    }
    const months: { key: string; start: Dayjs; label: string }[] = [];
    const monthAnchor = anchor.startOf('month');
    for (let i = 0; i < monthCount; i++) {
      const start = monthAnchor.add(i, 'month');
      months.push({ key: start.format('YYYY-MM'), start, label: start.format('MMM YYYY') });
    }
    return months;
  }, [anchor, zoom, weekCount, monthCount]);

  // Gruppierung der KW-Spalten nach Monat (für die obere Header-Reihe)
  const headerGroups = useMemo(() => {
    if (zoom === 'month') return null;
    const groups: { key: string; label: string; columnCount: number }[] = [];
    for (const col of columns) {
      const groupKey = col.start.format('YYYY-MM');
      const last = groups[groups.length - 1];
      if (last && last.key === groupKey) {
        last.columnCount += 1;
      } else {
        groups.push({
          key: groupKey,
          label: col.start.format('MMMM YYYY'),
          columnCount: 1,
        });
      }
    }
    return groups;
  }, [columns, zoom]);

  const rangeStart = columns[0]?.start ?? anchor;
  const rangeEnd =
    zoom === 'week'
      ? columns[columns.length - 1]?.start.add(6, 'day') ?? anchor
      : columns[columns.length - 1]?.start.endOf('month') ?? anchor;
  const totalDays = rangeEnd.diff(rangeStart, 'day') + 1;

  const filters: GanttFilters = useMemo(
    () => ({
      project_manager_id: pmFilter || undefined,
      site_manager_id: smFilter || undefined,
      include_tasks: showTasks,
      from: rangeStart.format('YYYY-MM-DD'),
      to: rangeEnd.format('YYYY-MM-DD'),
    }),
    [pmFilter, smFilter, showTasks, rangeStart, rangeEnd],
  );

  const gantt = useGantt(filters);
  const users = useUsersLookup();

  const today = dayjs();
  const showToday = today.isAfter(rangeStart) && today.isBefore(rangeEnd);
  const todayPercent = showToday ? (today.diff(rangeStart, 'day') / totalDays) * 100 : null;

  const handleToday = () => setAnchor(zoom === 'week' ? dayjs().startOf('isoWeek') : dayjs().startOf('month'));
  const handlePrev = () => setAnchor(anchor.subtract(zoom === 'week' ? 4 : 2, zoom));
  const handleNext = () => setAnchor(anchor.add(zoom === 'week' ? 4 : 2, zoom));

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Terminprogramm</div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={handlePrev}>← Zurück</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleToday}>Heute</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleNext}>Weiter →</button>
          <button
            type="button"
            className={`btn ${zoom === 'week' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setZoom('week')}
          >
            Wochen
          </button>
          <button
            type="button"
            className={`btn ${zoom === 'month' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setZoom('month')}
          >
            Monate
          </button>
        </div>
      </header>

      <div className="content">
        <div className="filter-bar" style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          <select value={pmFilter} onChange={(e) => setPmFilter(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Alle Projektleiter</option>
            {users.data
              ?.filter((u) => u.role === 'project_manager' || u.role === 'admin')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
          </select>
          <select value={smFilter} onChange={(e) => setSmFilter(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Alle Bauleiter</option>
            {users.data
              ?.filter((u) => u.role === 'site_manager' || u.role === 'admin')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'none', letterSpacing: 0 }}>
            <input type="checkbox" checked={showTasks} onChange={(e) => setShowTasks(e.target.checked)} style={{ width: 14, height: 14 }} />
            Detail-Termine
          </label>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            {rangeStart.format('DD.MM.YYYY')} — {rangeEnd.format('DD.MM.YYYY')}
          </div>
        </div>

        {gantt.isLoading ? (
          <div className="loading">Lade Terminprogramm …</div>
        ) : gantt.error ? (
          <div className="empty-state"><p>{apiErrorMessage(gantt.error)}</p></div>
        ) : (gantt.data?.items ?? []).length === 0 ? (
          <div className="empty-state"><p>Keine Projekte im aktuellen Filter.</p></div>
        ) : (
          <>
          <div className="mobile-scroll-hint">
            <span>↔</span>
            <span>Wischen, um alle Wochen zu sehen — oder Gerät querhalten.</span>
          </div>
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              {/* Inner-Track-Wrapper: feste minWidth, damit Monats- und KW-Header
                  und alle Rows synchron horizontal scrollen */}
              <div
                style={{
                  minWidth: LABEL_WIDTH_PX
                    + columns.length * (zoom === 'week' ? COLUMN_MIN_WIDTH_WEEK : COLUMN_MIN_WIDTH_MONTH),
                }}
              >
                {headerGroups && (
                  <div style={{ display: 'flex', background: 'var(--color-navy)', color: '#fff', borderBottom: '1px solid var(--color-border-soft)' }}>
                    <div style={{ width: 'var(--gantt-label-width)', minWidth: 'var(--gantt-label-width)', padding: '10px 18px', fontSize: 'var(--gantt-header-font-size)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, borderRight: '1px solid rgba(255,255,255,.1)', opacity: 0.85 }}>
                      Projekt
                    </div>
                    <div style={{ display: 'flex', flex: 1 }}>
                      {headerGroups.map((g) => (
                        <div
                          key={g.key}
                          style={{
                            flex: g.columnCount,
                            padding: '10px 8px',
                            fontSize: 'var(--gantt-group-font-size)',
                            fontWeight: 600,
                            textAlign: 'center',
                            borderRight: '1px solid rgba(255,255,255,.15)',
                            textTransform: 'capitalize',
                            letterSpacing: 0.3,
                          }}
                        >
                          {g.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="gantt-header" style={{ display: 'flex', background: 'var(--color-surface-alt)', borderBottom: '2px solid var(--color-border-soft)' }}>
                  <div style={{ width: 'var(--gantt-label-width)', minWidth: 'var(--gantt-label-width)', padding: '12px 18px', fontSize: 'var(--gantt-header-font-size)', fontWeight: 600, color: 'var(--color-text-soft)', textTransform: 'uppercase', letterSpacing: 0.5, borderRight: '1px solid var(--color-border-soft)' }}>
                    {headerGroups ? '' : 'Projekt'}
                  </div>
                  <div style={{ display: 'flex', flex: 1 }}>
                    {columns.map((col) => (
                      <div
                        key={col.key}
                        style={{
                          flex: 1,
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: 'var(--gantt-header-font-size)',
                          fontWeight: 600,
                          color: 'var(--color-text-soft)',
                          borderRight: '1px solid var(--color-divider)',
                        }}
                      >
                        {col.label}
                      </div>
                    ))}
                  </div>
                </div>

                {(gantt.data?.items ?? []).map((p) => (
                  <GanttRow
                    key={p.id}
                    project={p}
                    rangeStart={rangeStart}
                    totalDays={totalDays}
                    todayPercent={todayPercent}
                    showTasks={showTasks}
                  />
                ))}

                <UtilisationRow
                  utilisation={gantt.data?.utilisation ?? []}
                  rangeStart={rangeStart}
                  totalDays={totalDays}
                  todayPercent={todayPercent}
                />
              </div>
            </div>

            {/* Range-Controller außerhalb des Scroll-Wrappers — bleibt sichtbar */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 10,
                padding: '12px 20px',
                borderTop: '1px solid var(--color-border-soft)',
                background: 'var(--color-surface-alt)',
                fontSize: 13,
              }}
            >
              <span style={{ color: 'var(--color-text-muted)' }}>
                Zeitraum: <strong style={{ color: 'var(--color-text-strong)', fontVariantNumeric: 'tabular-nums' }}>
                  {zoom === 'week' ? `${weekCount} Wochen` : `${monthCount} Monate`}
                </strong>
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                title={zoom === 'week' ? `${WEEK_STEP} Wochen entfernen` : `${MONTH_STEP} Monate entfernen`}
                disabled={
                  zoom === 'week' ? weekCount <= WEEK_MIN : monthCount <= MONTH_MIN
                }
                onClick={() => {
                  if (zoom === 'week') {
                    setWeekCount((c) => Math.max(WEEK_MIN, c - WEEK_STEP));
                  } else {
                    setMonthCount((c) => Math.max(MONTH_MIN, c - MONTH_STEP));
                  }
                }}
                style={{ minWidth: 38, justifyContent: 'center', fontWeight: 700, fontSize: 16 }}
              >
                −
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                title={zoom === 'week' ? `${WEEK_STEP} Wochen hinzufügen` : `${MONTH_STEP} Monate hinzufügen`}
                disabled={
                  zoom === 'week' ? weekCount >= WEEK_MAX : monthCount >= MONTH_MAX
                }
                onClick={() => {
                  if (zoom === 'week') {
                    setWeekCount((c) => Math.min(WEEK_MAX, c + WEEK_STEP));
                  } else {
                    setMonthCount((c) => Math.min(MONTH_MAX, c + MONTH_STEP));
                  }
                }}
                style={{ minWidth: 38, justifyContent: 'center', fontWeight: 700, fontSize: 16 }}
              >
                +
              </button>
            </div>
          </div>
          </>
        )}
      </div>
    </>
  );
}

function calcBarRange(start: Dayjs | null, end: Dayjs | null, rangeStart: Dayjs, totalDays: number) {
  if (!start || !end) return null;
  const startOffset = Math.max(0, start.diff(rangeStart, 'day'));
  const endOffset = Math.min(totalDays, end.diff(rangeStart, 'day') + 1);
  if (endOffset <= 0 || startOffset >= totalDays) return null;
  return {
    left: (startOffset / totalDays) * 100,
    width: ((endOffset - startOffset) / totalDays) * 100,
  };
}

interface GanttRowProps {
  project: GanttProject;
  rangeStart: Dayjs;
  totalDays: number;
  todayPercent: number | null;
  showTasks: boolean;
}

/**
 * Eine "Projekt-Gruppe" im Gantt: Header-Zeile mit Projekt-Info links
 * und unter dem Projekt eine eigene Zeile pro Sektion (Haus 1, Haus 2,
 * …). Jede Sektion-Zeile zeigt die Aufgaben als zwei gestapelte Balken:
 *   - oben:  Bauzeit (schedule_start/end) — immer blau
 *   - unten: Unser   (internal_start/end) — in Status-Farbe der Aufgabe
 * Aufgaben ohne Daten werden weggelassen (kommen aus der Backend-Query
 * sowieso nicht zurück).
 */
function GanttRow({ project, rangeStart, totalDays, todayPercent, showTasks }: GanttRowProps) {
  // Wenn der User Detail-Termine ausschaltet, gibt es nichts zu zeigen
  // — wir rendern nur eine reduzierte Header-Zeile mit Projekt-Info.
  const sections = showTasks ? project.sections ?? [] : [];

  const projectRange = calcBarRange(
    project.start_date ? dayjs(project.start_date) : null,
    project.end_date ? dayjs(project.end_date) : null,
    rangeStart,
    totalDays,
  );
  const projectBarTitle =
    project.start_date && project.end_date
      ? `${project.title} — ${dayjs(project.start_date).format('DD.MM.YYYY')} bis ${dayjs(project.end_date).format('DD.MM.YYYY')}`
      : project.title;

  return (
    <>
      {/* Projekt-Header-Zeile mit eigenem Balken (Start/Ende aus Projekt-Stammdaten). */}
      <div
        className="gantt-project-header"
        style={{
          display: 'flex',
          background: 'var(--color-surface-alt)',
          borderTop: '1px solid var(--color-border-soft)',
          borderBottom: '1px solid var(--color-divider)',
          minHeight: 44,
          alignItems: 'center',
        }}
      >
        <div
          className="gantt-info"
          style={{
            width: 'var(--gantt-label-width)',
            minWidth: 'var(--gantt-label-width)',
            padding: '10px 18px',
            borderRight: '1px solid var(--color-border-soft)',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Link
            to={`/projects/${project.id}`}
            className="gantt-project"
            style={{
              fontSize: 'var(--gantt-project-font-size)',
              fontWeight: 700,
              color: 'var(--color-navy)',
            }}
          >
            {project.title}
          </Link>
          <div className="gantt-meta" style={{ fontSize: 'var(--gantt-meta-font-size)', color: 'var(--color-text-soft)' }}>
            {project.order_number}
            {project.project_manager_abbr ? ` · PL ${project.project_manager_abbr}` : ''}
            {project.site_manager_abbr ? ` · BL ${project.site_manager_abbr}` : ''}
          </div>
        </div>
        <div style={{ flex: 1, position: 'relative', height: 44 }}>
          {todayPercent != null && (
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${todayPercent}%`, width: 2, background: 'var(--color-primary-light)', opacity: 0.6 }} />
          )}
          {projectRange && (
            <Link
              to={`/projects/${project.id}`}
              title={projectBarTitle}
              style={{
                position: 'absolute',
                left: `${projectRange.left}%`,
                width: `${Math.max(0.6, projectRange.width)}%`,
                minWidth: 8,
                top: 12,
                height: 20,
                borderRadius: 5,
                background: SCHEDULE_BAR_COLOR,
                opacity: 0.85,
                boxShadow: '0 1px 2px rgba(0,0,0,.18)',
                cursor: 'pointer',
              }}
            />
          )}
          {!showTasks && !projectRange && (
            <span style={{ marginLeft: 14, fontSize: 12, color: 'var(--color-text-soft)' }}>
              Detail-Termine deaktiviert
            </span>
          )}
        </div>
      </div>

      {sections.map((section) => (
        <GanttSectionRow
          key={section.id}
          projectId={project.id}
          section={section}
          rangeStart={rangeStart}
          totalDays={totalDays}
          todayPercent={todayPercent}
        />
      ))}

      {showTasks && sections.length === 0 && (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-divider)', minHeight: 32 }}>
          <div style={{ width: 'var(--gantt-label-width)', minWidth: 'var(--gantt-label-width)', padding: '6px 32px', fontSize: 12, color: 'var(--color-text-soft)' }}>
            Keine Sektionen
          </div>
          <div style={{ flex: 1 }} />
        </div>
      )}
    </>
  );
}

interface GanttSectionRowProps {
  projectId: number;
  section: GanttSection;
  rangeStart: Dayjs;
  totalDays: number;
  todayPercent: number | null;
}

const SECTION_ROW_HEIGHT = 30;
const SECTION_BAR_HEIGHT = 10;
const SECTION_BAR_GAP = 2;

function GanttSectionRow({ projectId, section, rangeStart, totalDays, todayPercent }: GanttSectionRowProps) {
  const tasks = section.tasks;
  return (
    <div
      className="gantt-section-row"
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--color-divider)',
        minHeight: SECTION_ROW_HEIGHT,
        alignItems: 'stretch',
      }}
    >
      <div
        style={{
          width: 'var(--gantt-label-width)',
          minWidth: 'var(--gantt-label-width)',
          padding: '4px 18px 4px 32px',
          borderRight: '1px solid var(--color-border-soft)',
          display: 'flex',
          alignItems: 'center',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <Link
          to={`/projects/${projectId}?section=${section.id}`}
          style={{
            color: 'var(--color-text)',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-navy)'; e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text)'; e.currentTarget.style.textDecoration = 'none'; }}
        >
          {section.name}
        </Link>
      </div>
      <div
        style={{
          flex: 1,
          position: 'relative',
          minHeight: SECTION_ROW_HEIGHT,
        }}
      >
        {todayPercent != null && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${todayPercent}%`, width: 2, background: 'var(--color-primary-light)', opacity: 0.6 }} />
        )}
        {tasks.length === 0 ? (
          <span style={{ marginLeft: 14, fontSize: 11, color: 'var(--color-text-soft)', lineHeight: `${SECTION_ROW_HEIGHT}px` }}>
            Keine Termine
          </span>
        ) : (
          tasks.map((task) => (
            <GanttTaskBars
              key={task.id}
              projectId={projectId}
              task={task}
              rangeStart={rangeStart}
              totalDays={totalDays}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface GanttTaskBarsProps {
  projectId: number;
  task: GanttTask;
  rangeStart: Dayjs;
  totalDays: number;
}

/** Rendert pro Aufgabe zwei gestapelte Balken — oben Bauzeit (blau),
 *  unten Unser-Termin (status-farbig). Wenn eines der Datums-Paare
 *  fehlt, wird der entsprechende Balken weggelassen. */
function GanttTaskBars({ projectId, task, rangeStart, totalDays }: GanttTaskBarsProps) {
  const scheduleRange = calcBarRange(
    task.schedule_start ? dayjs(task.schedule_start) : null,
    task.schedule_end ? dayjs(task.schedule_end) : null,
    rangeStart,
    totalDays,
  );
  const internalRange = calcBarRange(
    task.internal_start ? dayjs(task.internal_start) : null,
    task.internal_end ? dayjs(task.internal_end) : null,
    rangeStart,
    totalDays,
  );

  // UI-Verzug: schedule_end überschritten + nicht abgeschlossen, auch wenn
  // Status nicht explizit "delayed" ist (analog zur Detail-Tabelle).
  const delayed = isTaskDelayed({ schedule_end: task.schedule_end, status: task.status });
  const internalColor = delayed
    ? STATUS_INTERNAL_COLOR.delayed
    : STATUS_INTERNAL_COLOR[task.status] ?? STATUS_INTERNAL_COLOR.open;

  const link = `/projects/${projectId}?section=${task.section_id}&task=${task.id}`;
  const scheduleTitle = `${task.title} — Bauzeit ${dayjs(task.schedule_start ?? undefined).format('DD.MM.YYYY')} bis ${dayjs(task.schedule_end ?? undefined).format('DD.MM.YYYY')}`;
  const internalTitle = `${task.title} — Unser ${dayjs(task.internal_start ?? undefined).format('DD.MM.YYYY')} bis ${dayjs(task.internal_end ?? undefined).format('DD.MM.YYYY')}`;

  // Zwei vertikale Stop-Positionen für die zwei Balken
  const topRow = (SECTION_ROW_HEIGHT - 2 * SECTION_BAR_HEIGHT - SECTION_BAR_GAP) / 2;
  const bottomRow = topRow + SECTION_BAR_HEIGHT + SECTION_BAR_GAP;

  return (
    <>
      {scheduleRange && (
        <Link
          to={link}
          title={scheduleTitle}
          style={{
            position: 'absolute',
            left: `${scheduleRange.left}%`,
            width: `${Math.max(0.6, scheduleRange.width)}%`,
            minWidth: 8,
            top: topRow,
            height: SECTION_BAR_HEIGHT,
            borderRadius: 4,
            background: SCHEDULE_BAR_COLOR,
            opacity: 0.95,
            boxShadow: '0 1px 2px rgba(0,0,0,.18)',
            cursor: 'pointer',
            transition: 'transform .12s, box-shadow .12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scaleY(1.18)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,.28)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scaleY(1)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,.18)';
          }}
        />
      )}
      {internalRange && (
        <Link
          to={link}
          title={internalTitle}
          style={{
            position: 'absolute',
            left: `${internalRange.left}%`,
            width: `${Math.max(0.6, internalRange.width)}%`,
            minWidth: 8,
            top: bottomRow,
            height: SECTION_BAR_HEIGHT,
            borderRadius: 4,
            background: internalColor,
            opacity: 0.95,
            boxShadow: '0 1px 2px rgba(0,0,0,.18)',
            cursor: 'pointer',
            transition: 'transform .12s, box-shadow .12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scaleY(1.18)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,.28)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scaleY(1)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,.18)';
          }}
        />
      )}
    </>
  );
}

interface UtilisationRowProps {
  utilisation: GanttUtilisation[];
  rangeStart: Dayjs;
  totalDays: number;
  todayPercent: number | null;
}

function UtilisationRow({ utilisation, rangeStart, totalDays, todayPercent }: UtilisationRowProps) {
  if (utilisation.length === 0) return null;
  const max = Math.max(1, ...utilisation.map((u) => u.project_count));

  return (
    <div
      style={{
        display: 'flex',
        borderTop: '2px solid var(--color-border-soft)',
        background: 'var(--color-surface-alt)',
        minHeight: 'var(--gantt-utilisation-height)',
      }}
    >
      <div
        style={{
          width: 'var(--gantt-label-width)',
          minWidth: 'var(--gantt-label-width)',
          padding: '10px 18px',
          fontSize: 'var(--gantt-header-font-size)',
          fontWeight: 600,
          color: 'var(--color-text-soft)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          borderRight: '1px solid var(--color-border-soft)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        Auslastung (aktive Projekte / KW)
      </div>
      <div style={{ flex: 1, position: 'relative', height: 'var(--gantt-utilisation-height)' }}>
        {todayPercent != null && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${todayPercent}%`, width: 2, background: 'var(--color-primary-light)', opacity: 0.6 }} />
        )}
        {utilisation.map((u) => {
          const start = dayjs(u.week_start);
          const range = calcBarRange(start, start.add(6, 'day'), rangeStart, totalDays);
          if (!range) return null;
          const intensity = u.project_count / max;
          return (
            <div
              key={u.week_start}
              title={`${start.format('DD.MM.YYYY')}: ${u.project_count} Projekte`}
              style={{
                position: 'absolute',
                left: `${range.left}%`,
                width: `${range.width}%`,
                top: 5,
                bottom: 5,
                background: u.project_count === 0 ? 'transparent' : `rgba(24, 95, 165, ${0.15 + intensity * 0.6})`,
                borderRight: '1px solid var(--color-divider)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: intensity > 0.5 ? '#fff' : 'var(--color-text-muted)',
              }}
            >
              {u.project_count > 0 ? u.project_count : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}
